import type { FC, ReactNode } from 'react';
import React, { useCallback, useRef } from 'react';
import { defineMessages, FormattedDate, FormattedMessage } from 'react-intl-next';
// We have deprecated unstated. Please use react-sweet-state instead
import { Subscribe } from 'unstated';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { Box, Text } from '@atlaskit/primitives/compiled';
import { cssMap } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';

import { AutoUpgradeAvoidDeactivationModal } from '@atlassian/auto-upgrade-not-a-trial';

import { ConfluenceEdition } from '@confluence/change-edition/entry-points/ConfluenceEdition.ts';
import { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import type { FlagDescriptor } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import { linkToAdminHubOrg } from '../utils/getAdminHubUrl.ts';
import { usePremiumReverseTrialABCohort } from '../PremiumReverseTrialsExperiment/useIsEligibleForPremiumReverseTrialsExperiment.ts';

const FREE_USER_LIMIT = 10;

type TrialFlagsComponentProps = {
	daysLeftInPredunning?: number;
	deactivationDate?: Date;
	edition: ConfluenceEdition | null;
	licenseConsumingUserCount: number;
	linkToAddBilling: string;
	cloudId: string;
	onDismiss: () => void;
	trialDaysLeft: number | null;
	isEligibleForPremiumReverseTrialsExperiment: boolean;
	planSelectionPageUrl: string | undefined;
	autoUpgradeNotATrialExperimentCohort: string;
};

type Action = {
	content: ReactNode;
	onClick?: () => void | undefined;
	href?: string | undefined;
	target?: string | undefined;
};

const linkStyles = cssMap({
	modalTakeAction: {
		color: token('color.link'),
		'&:hover': {
			textDecorationLine: 'underline',
			cursor: 'pointer',
		},
	},
});

const i18n = defineMessages({
	titlePremium: {
		id: 'trial-components.flag.title.premium',
		defaultMessage:
			'Your Premium trial ends in {trialDaysLeft, plural, one {# day} other {# days}}',
		description: 'Title how many days are left on the trial',
	},
	titleStandard: {
		id: 'trial-components.flag.title.standard',
		defaultMessage:
			'Your Standard trial ends in {trialDaysLeft, plural, one {# day} other {# days}}',
		description: 'Title how many days are left on the trial',
	},
	removeUsersLinkText: {
		id: 'trial-components.flag.remove-users.link-text',
		defaultMessage: 'Remove users',
		description:
			'Link text that directs users to a page where they can remove users from their site to comply with free plan limits',
	},
	choosePlanLinkText: {
		id: 'trial-components.flag.choose-plan.link-text',
		defaultMessage: 'Choose plan',
		description: 'Link to plan selection page on Commerce Experience',
	},
	infoDescription: {
		id: 'trial-components.flag.info.description',
		defaultMessage:
			'Add payment details to stay on {edition} and keep the features you love. Otherwise, your site will be downgraded to Confluence Free.',
		description:
			'Telling a user to add their payment details in order to keep the features on their current edition. {edition} will be either `Premium` or `Standard`',
	},
	warningDescription: {
		id: 'trial-components.flag.warning.description',
		defaultMessage:
			'Add payment details to stay on {edition} and keep more than 10 users, or remove users and switch to Confluence Free. <strong>If you don’t take action, your site will be deactivated on {deactivationDateLong}.</strong>',
		description:
			'Telling a user to add their payment details in order to keep the features on their current edition. {edition} will be either `Premium` or `Standard`',
	},
	addPaymentDetailsCTA: {
		id: 'trial-components.flag.add-payment-details',
		defaultMessage: 'Add payment details',
		description: 'Link to adding the payment details for Confluence',
	},
	reverseTrialsTitleOverUserLimit3DaysLeft: {
		id: 'trial-components.flag.rt-title.overUserLimit.three-days-left',
		defaultMessage: 'Choose a plan to avoid deactivation',
		description: 'Advising user to choose a plan to avoid deactivation of their site',
	},
	reverseTrialsTitleOverUserLimit1DaysLeft: {
		id: 'trial-components.flag.rt-title.overUserLimit.one-day-left',
		defaultMessage: 'Choose a plan to avoid deactivation',
		description: 'Advising user to choose a plan to avoid deactivation of their site',
	},
	reverseTrialsTitleUnderUserLimit3DaysLeft: {
		id: 'trial-components.flag.rt-title.underUserLimit.three-days-left',
		defaultMessage:
			'Final stretch! Your trial ends in {trialDaysLeft, plural, one {# day} other {# days}}',
		description: 'Advising user that their trial is ending soon',
	},
	reverseTrialsTitleUnderUserLimit1DaysLeft: {
		id: 'trial-components.flag.rt-title.underUserLimit.one-day-left',
		defaultMessage:
			'Time’s almost up! Your trial ends in {trialDaysLeft, plural, one {# day} other {# days}}',
		description: 'Advising user that their trial is ending very soon',
	},
	reverseTrialsDescriptionOverUserLimit3DaysLeft: {
		id: 'trial-components.flag.rt-description.overUserLimit.three-days-left',
		defaultMessage:
			'You have more than {freeUserLimit} users, so you’ll need to choose a paid plan or remove users by {deactivationDateLong}.',
		description:
			'Telling a user to choose a paid plan or remove users by their deactivation date, otherwise their site will be deactivated',
	},
	reverseTrialsDescriptionOverUserLimit1DaysLeft: {
		id: 'trial-components.flag.rt-description.overUserLimit.one-day-left',
		defaultMessage:
			'You have more than {freeUserLimit} users, so you’ll need to choose a paid plan or remove users by {deactivationDateLong}.',
		description:
			'Telling a user to choose a paid plan or remove users by their deactivation date, otherwise their site will be deactivated',
	},
	reverseTrialsDescriptionUnderUserLimit3DaysLeft: {
		id: 'trial-components.flag.rt-description.underUserLimit.three-days-left',
		defaultMessage:
			'See pricing and choose a plan, or you’ll be downgraded to a free plan with limited features.',
		description:
			'Advising user to choose a plan to avoid being downgraded to a free plan with limited features',
	},
	reverseTrialsDescriptionUnderUserLimit1DaysLeft: {
		id: 'trial-components.flag.rt-description.underUserLimit.one-day-left',
		defaultMessage:
			'See pricing and choose a plan, or you’ll be downgraded to a free plan with limited features.',
		description:
			'Advising user to choose a plan to avoid being downgraded to a free plan with limited features',
	},
	autoUpgradeTitleLastDayOfPredunning: {
		id: 'trial-components.flag.auto-upgrade.title-last-day-of-predunning',
		defaultMessage: 'Take action, avoid deactivation',
		description:
			'Warning flag title for users over the Free user limit, prompting them to add payment or remove users to avoid deactivation',
	},
	autoUpgradeTitleWithDaysLeft: {
		id: 'trial-components.flag.auto-upgrade.title-with-days-left',
		// eslint-disable-next-line @atlassian/i18n/enforce-plural-rules
		defaultMessage: '{trialDaysLeft, plural, =1 {# day} other {# days}} left to avoid deactivation',
		description:
			'Warning flag title for users over the Free user limit, prompting them to add payment or remove users to avoid deactivation',
	},
	autoUpgradeDescription: {
		id: 'trial-components.flag.auto-upgrade.description',
		defaultMessage: 'Add payment or remove users by {deactivationDate} to avoid site deactivation.',
		description:
			'Warning flag message for users over the Free user limit, prompting them to add payment or remove users to avoid deactivation',
	},
	takeActionCTA: {
		id: 'trial-components.flag.auto-upgrade.take-action',
		defaultMessage: 'Take action',
		description:
			'Link text in the auto-upgrade avoid deactivation banner, prompting the user to take action to avoid deactivation of their product',
	},
});

const LINK_TO_REMOVE_USERS =
	'https://support.atlassian.com/confluence-cloud/docs/removing-users-and-downgrading-to-confluence-free/';

type TrialFlagsComponentInnerProps = TrialFlagsComponentProps & {
	flags: FlagsStateContainer;
};

const USER_ACTION_REMOVE_USER = 'removeUser';
const USER_ACTION_ADD_BILLING = 'addBilling';
const USER_ACTION_CHOOSE_PLAN = 'choosePlan';
const USER_ACTION_OPEN_AUTO_UPGRADE_DEACTIVATION_MODAL = 'openAutoUpgradeDeactivationModal';

const getActionAnalyticsEvent = (
	edition: string | null,
	type: string,
	userAction: string,
	autoUpgradeNotATrialExperimentCohort: string,
	trialDaysLeft: number | null,
	daysLeftInPredunning?: number,
	premiumReverseTrialABCohort?: string,
) => ({
	type: 'sendUIEvent',
	data: {
		action: 'clicked',
		actionSubject: 'button',
		actionSubjectId: 'trialActionComponent',
		attributes: {
			edition,
			trialDaysLeft,
			daysLeftInPredunning,
			userAction,
			type,
			premiumReverseTrialABCohort,
			autoUpgradeNotATrialExperimentCohort,
		},
		source: 'trialFlag',
	},
});

const TrialFlagsComponentInner: FC<TrialFlagsComponentInnerProps> = ({
	daysLeftInPredunning,
	deactivationDate,
	edition,
	flags,
	licenseConsumingUserCount,
	linkToAddBilling,
	cloudId,
	onDismiss: onDismissStoreFlagOnboardingState,
	trialDaysLeft,
	isEligibleForPremiumReverseTrialsExperiment,
	planSelectionPageUrl,
	autoUpgradeNotATrialExperimentCohort,
}) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { orgId, environment } = useSessionData();

	const isWarn = licenseConsumingUserCount > 10;
	const type = isWarn ? 'warning' : 'info';
	let flagId = `${edition}-trial-${trialDaysLeft}-day-${type}-flag`;

	const premiumReverseTrialABCohort = usePremiumReverseTrialABCohort({
		isEligible: isEligibleForPremiumReverseTrialsExperiment,
		touchpointId: 'trial-flag',
		attributes: {
			planSelectionPageUrl,
			trialDaysLeft,
			deactivationDate: deactivationDate?.toISOString(),
			licenseConsumingUserCount,
		},
	});

	const hasShownFlagRef = useRef(false);
	const flagIdRef = useRef<string | number | null>(null);
	const hasFiredAnalyticsRef = useRef(false);

	const onDismiss = useCallback(() => {
		flagIdRef.current && void flags.hideFlag(flagIdRef.current);
		onDismissStoreFlagOnboardingState();
	}, [flags, onDismissStoreFlagOnboardingState]);

	const handleAddBillingClick = useCallback(() => {
		createAnalyticsEvent(
			getActionAnalyticsEvent(
				edition,
				type,
				USER_ACTION_ADD_BILLING,
				autoUpgradeNotATrialExperimentCohort,
				trialDaysLeft,
				daysLeftInPredunning,
				premiumReverseTrialABCohort,
			),
		).fire();
	}, [
		edition,
		trialDaysLeft,
		type,
		createAnalyticsEvent,
		premiumReverseTrialABCohort,
		autoUpgradeNotATrialExperimentCohort,
		daysLeftInPredunning,
	]);

	const handleRemoveUsersClick = useCallback(() => {
		createAnalyticsEvent(
			getActionAnalyticsEvent(
				edition,
				type,
				USER_ACTION_REMOVE_USER,
				autoUpgradeNotATrialExperimentCohort,
				trialDaysLeft,
				daysLeftInPredunning,
				premiumReverseTrialABCohort,
			),
		).fire();
	}, [
		edition,
		trialDaysLeft,
		type,
		createAnalyticsEvent,
		premiumReverseTrialABCohort,
		autoUpgradeNotATrialExperimentCohort,
		daysLeftInPredunning,
	]);

	const handleChoosePlanClick = useCallback(() => {
		createAnalyticsEvent(
			getActionAnalyticsEvent(
				edition,
				type,
				USER_ACTION_CHOOSE_PLAN,
				autoUpgradeNotATrialExperimentCohort,
				trialDaysLeft,
				daysLeftInPredunning,
				premiumReverseTrialABCohort,
			),
		).fire();
	}, [
		edition,
		trialDaysLeft,
		type,
		createAnalyticsEvent,
		premiumReverseTrialABCohort,
		autoUpgradeNotATrialExperimentCohort,
		daysLeftInPredunning,
	]);

	const handleTakeActionClick = useCallback(() => {
		createAnalyticsEvent(
			getActionAnalyticsEvent(
				edition,
				type,
				USER_ACTION_OPEN_AUTO_UPGRADE_DEACTIVATION_MODAL,
				autoUpgradeNotATrialExperimentCohort,
				trialDaysLeft,
				daysLeftInPredunning,
				premiumReverseTrialABCohort,
			),
		).fire();
	}, [
		edition,
		trialDaysLeft,
		type,
		createAnalyticsEvent,
		premiumReverseTrialABCohort,
		autoUpgradeNotATrialExperimentCohort,
		daysLeftInPredunning,
	]);

	const addPaymentDetails: Action = {
		content: <FormattedMessage {...i18n.addPaymentDetailsCTA} />,
		href: linkToAddBilling,
		target: '_blank',
		onClick: handleAddBillingClick,
	};

	const removeUsersAction: Action = {
		content: <FormattedMessage {...i18n.removeUsersLinkText} />,
		href: LINK_TO_REMOVE_USERS,
		target: '_blank',
		onClick: handleRemoveUsersClick,
	};

	const removeUsersNavigateToOrgInAdminHub: Action = {
		content: <FormattedMessage {...i18n.removeUsersLinkText} />,
		href: linkToAdminHubOrg(environment, orgId, '/users?status=ACTIVE'),
		target: '_blank',
		onClick: handleRemoveUsersClick,
	};

	const choosePlanLink: Action = {
		content: <FormattedMessage {...i18n.choosePlanLinkText} />,
		href: planSelectionPageUrl,
		target: '_blank',
		onClick: handleChoosePlanClick,
	};

	const autoUpgradeTakeActionContent = (
		<AutoUpgradeAvoidDeactivationModal
			product="confluence"
			touchpointId="avoid-deactivation-flag"
			cloudId={cloudId}
		>
			<Box
				as="span"
				xcss={linkStyles.modalTakeAction}
				testId="auto-upgrade-avoid-deactivation-flag-take-action-button"
			>
				<FormattedMessage {...i18n.takeActionCTA} />
			</Box>
		</AutoUpgradeAvoidDeactivationModal>
	);

	const autoUpgradeTakeAction: Action = {
		content: autoUpgradeTakeActionContent,
		onClick: handleTakeActionClick,
	};

	const editionKey = edition === ConfluenceEdition.STANDARD ? 'Standard' : 'Premium';

	let flag: FlagDescriptor | null = null;

	if (premiumReverseTrialABCohort === 'variation' && deactivationDate !== undefined) {
		const userLimitDescriptor = licenseConsumingUserCount > FREE_USER_LIMIT ? 'Over' : 'Under';
		const rtTitle = `reverseTrialsTitle${userLimitDescriptor}UserLimit${trialDaysLeft}DaysLeft`;
		const rtDescription = `reverseTrialsDescription${userLimitDescriptor}UserLimit${trialDaysLeft}DaysLeft`;
		flagId = `${flagId}-reverse-trial`;

		flag = {
			id: flagId,
			type,
			title: <FormattedMessage {...i18n[rtTitle]} values={{ trialDaysLeft }} />,
			description: (
				<FormattedMessage
					{...i18n[rtDescription]}
					{...{
						values: isWarn
							? {
									deactivationDateLong: (
										<FormattedDate value={deactivationDate} month="long" day="numeric" />
									),
									freeUserLimit: FREE_USER_LIMIT,
								}
							: {},
					}}
				/>
			),
			actions: isWarn ? [choosePlanLink, removeUsersNavigateToOrgInAdminHub] : [choosePlanLink],
			onClose: onDismiss,
		};
	} else if (
		autoUpgradeNotATrialExperimentCohort === 'treatment' &&
		deactivationDate !== undefined &&
		daysLeftInPredunning !== undefined
	) {
		const title =
			daysLeftInPredunning === 0
				? i18n.autoUpgradeTitleLastDayOfPredunning
				: i18n.autoUpgradeTitleWithDaysLeft;

		flag = {
			id: flagId,
			type: 'warning',
			title: <FormattedMessage {...title} values={{ trialDaysLeft: daysLeftInPredunning }} />,
			description: (
				<FormattedMessage
					{...i18n.autoUpgradeDescription}
					values={{
						deactivationDate: (
							<Text weight="bold">
								<FormattedDate value={deactivationDate} month="long" day="numeric" />
							</Text>
						),
					}}
				/>
			),
			actions: [autoUpgradeTakeAction],
			onClose: onDismiss,
		};
	} else if (premiumReverseTrialABCohort !== undefined) {
		flag = {
			id: flagId,
			type,
			title: <FormattedMessage {...i18n[`title${editionKey}`]} values={{ trialDaysLeft }} />,
			description: (
				<FormattedMessage
					{...i18n[`${type}Description`]}
					{...{
						values: isWarn
							? {
									deactivationDateLong: (
										<FormattedDate value={deactivationDate} month="long" day="numeric" />
									),
									strong: (...chunks) => <Text as="strong">{chunks}</Text>,
									edition: editionKey,
								}
							: { edition: editionKey },
					}}
				/>
			),
			actions: isWarn ? [addPaymentDetails, removeUsersAction] : [addPaymentDetails],
			onClose: onDismiss,
		};
	}

	flagIdRef.current = flagId;

	if (hasShownFlagRef && !hasShownFlagRef.current && flag) {
		void flags.showFlag(flag).then((newFlag) => {
			// If a new flag was created, store the new flag's ID so we can update it.  Note that
			// `showFlag()` only asynchronously returns a valid newFlag if it created a new flag;
			// if it found an existing flag with the specified ID then it asynchronously returns
			// nothing (undefined).
			if (newFlag) {
				flagIdRef.current = newFlag.id;
			}
		});
		hasShownFlagRef.current = true;
	}

	if (!hasFiredAnalyticsRef.current && flag) {
		createAnalyticsEvent({
			type: 'sendScreenEvent',
			data: {
				name: 'trialFlagScreen',
				attributes: {
					edition,
					trialDaysLeft,
					daysLeftInPredunning,
					type,
					premiumReverseTrialABCohort,
					autoUpgradeNotATrialExperimentCohort,
				},
			},
		}).fire();
		// Mark the event as fired
		hasFiredAnalyticsRef.current = true;
	}
	return null;
};

export const TrialFlagsComponent: FC<TrialFlagsComponentProps> = (props) => {
	return (
		<Subscribe to={[FlagsStateContainer]}>
			{(flags: FlagsStateContainer) => {
				return <TrialFlagsComponentInner flags={flags} {...props} />;
			}}
		</Subscribe>
	);
};
